<template>
  <div>
    <CCard class="px-3">
      <CCardBody>
        <CRow
          class="
            justify-content-between
            align-items-start
            mb-2
            flex-row
            px-2
            mb-3
          "
        >
          <div class="d-flex align-items-center">
            <figure v-if="marketHaveLogo(market)" class="m-0 mr-3">
              <img
                @click="openMarket(market)"
                :src="marketLogoThumb(market)"
                alt=""
                class="border-0 rounded-circle cursor-pointer"
                width="64"
                height="64"
              />
            </figure>

            <div
              class="d-flex flex-column align-items-start align-items-md-center"
            >
              <span
                class="d-inline-block ui-type-display-lg mr-3 cursor-pointer"
                @click="openMarket(market)"
                >{{ marketName(market) }}</span
              >
            </div>
          </div>

          <div class="d-flex flex-row align-items-center">
            <div class="px-0 w-100 text-dark d-flex flex-row">
              <CCallout color="primary border-0 pl-1 mt-2 mb-0">
                <font-awesome-icon icon="clock" class="mr-2 text-primary" />
                <span v-if="timelimitToOrder(market, user_lang)"
                  >{{ $t("family.market.box.marketEventsClosure") }}
                  {{ timelimitToOrder(market, user_lang) }}</span
                ></CCallout
              >
              <CCallout color="primary border-0 pl-1 mt-2 mb-2">
                <font-awesome-icon icon="truck" class="mr-2 text-primary" />
                <span v-if="showDelivery(market)"
                  >{{ $t("family.market.showcase.delivery") }}
                  <strong>{{ deliveryDate(market) }}</strong></span
                >
                <span v-if="showPickup(market)"
                  >{{ $t("family.market.showcase.pickup") }}
                  <strong>{{ pickupDate(market) }}</strong></span
                ></CCallout
              >
              <CButton
                color="primary"
                size="sm"
                @click="openMarket(market)"
                class="text-uppercase px-3"
                >{{ $t("family.market.box.btn") }}</CButton
              >
            </div>
          </div>
        </CRow>

        <CRow class="mb-3">
          <CCol cols="12">
            <div class="d-flex justify-content-center" v-if="loading">
              <CSpinner color="info" />
            </div>

            <flickity
              :options="flickityOptions"
              class="flickity-products p-2"
              v-else
            >
              <CCol
                sm="6"
                md="4"
                lg="2"
                class="flickity-col"
                v-for="(product, index) in products"
                :key="`${product.id}-${index}`"
                style="cursor: pointer"
              >
                <MarketBoxMarketingProductCard
                  @click.native="onCardClick(product.ppl.productproducer.id)"
                  :product="product"
                  :flags="flags"
                />
              </CCol>
            </flickity>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import marketMixin from "../../../mixins/market";
import { apiGetRQL } from "../../../helpers/apiHelper";
import MarketProducerProfile from "./MarketProducerProfile.vue";
import get from "lodash/get";
import MarketBoxMarketingProductCard from "./MarketBoxMarketingProductCard.vue";

export default {
  name: "MarketBoxMarketing",

  mixins: [marketMixin],

  components: {
    MarketProducerProfile,
    MarketBoxMarketingProductCard,
  },

  props: {
    market: { type: Object, required: true },
  },

  data() {
    return {
      loading: true,
      products: [],
      producers: [],
      producerModal: {},
      producerModalVisible: false,
      flags: [
        { value: 1, label: this.$t("models.ppm.flags.options1") },
        { value: 2, label: this.$t("models.ppm.flags.option2") },
        { value: 3, label: this.$t("models.ppm.flags.option3") },
        { value: 4, label: this.$t("models.ppm.flags.option4") },
        { value: 5, label: this.$t("models.ppm.flags.option5") },
        { value: 6, label: this.$t("models.ppm.flags.option6") },
      ],
      flickityOptions: {
        imagesLoaded: true,
        pageDots: false,
        cellAlign: "left",
        groupCells: true,
        autoPlay: 5000,
      },
    };
  },

  mounted() {
    this.getProducts();
  },

  computed: {
    user_lang() {
      return this.$i18n.locale;
    },
    ...mapGetters("connections", ["currencySymbol", "familyId"]),
  },

  methods: {
    openMarket(market) {
      this.$store.commit("shop/setMarket", market);
      this.$store.commit("shop/setCart", {});
      this.$router.push({
        name: "UserMarketDetail",
        params: { familyId: this.familyId, marketId: market.market.id },
      });
    },

    onCardClick(variantId) {
      const familyId = this.familyId;
      const marketId = this.market.market.id;

      // Find the product from the variant
      const variant = this.products.find((p) => p.id === variantId);
      const productId = variant?.product?.id || variantId;

      this.$router.push({
        name: "UserMarketProduct",
        params: { familyId, marketId, productId },
      });
    },

    async getProducts() {
      this.loading = true;

      let rqlFilter = {};

      let flagIdString = "";
      this.flags.forEach((flag, index) => {
        flagIdString += `eq(flag,${flag.value})`;
        if (index < this.flags.length - 1) {
          flagIdString += ",";
        }
      });

      rqlFilter["flag"] = {
        style: "multiFunction",
        term: flagIdString,
        operator: "or",
      };

      rqlFilter.limit = {
        style: "query",
        operator: "=",
        term: 50,
      };

      let params = {
        endpoint: "family_market_variants",
        params: [
          this.$store.state.connections.current.family.id,
          this.market.market.id,
        ],
        filter: rqlFilter,
      };

      const response = await apiGetRQL(params);
      let products = [];

      if (response && response.data) {
        // First try to get flagged products
        if (response.data.payload.Items.length > 0) {
          products = response.data.payload.Items;
        } else {
          // If no flagged products, get any products
          let altParams = {
            endpoint: "family_market_variants",
            params: [
              this.$store.state.connections.current.family.id,
              this.market.market.id,
            ],
            filter: {
              limit: {
                style: "query",
                operator: "=",
                term: 20,
              },
            },
          };
          const altResponse = await apiGetRQL(altParams);
          if (altResponse && altResponse.data.payload.Items.length > 0) {
            products = altResponse.data.payload.Items;
          }
        }

        // Randomly shuffle and assign
        this.products = products.sort(() => Math.random() - 0.5);
      }

      this.loading = false;
    },
  },
};
</script>
